/**
 * Activity Logger Service
 * Registra actividades de usuario en localStorage para el historial de actividad
 */

export type ActivityAction = 'create' | 'update' | 'delete';
export type ActivityModule = 'Dashboards' | 'Fuentes de Datos' | 'Sistema';

export interface ActivityLog {
  id: string;
  user: string;
  action: string;
  date: string;
  module: ActivityModule;
  metadata: {
    name?: string;
    type: ActivityAction;
    entityId?: string | number;
    [key: string]: any;
  };
}

const ACTIVITY_STORAGE_KEY = 'aion_activity_log';
const MAX_ACTIVITIES = 500; // Mantener solo las últimas 500 actividades

/**
 * Obtiene el usuario actual del localStorage
 */
const getCurrentUser = (): string => {
  try {
    const userStr = localStorage.getItem('currentUser');
    if (userStr) {
      const user = JSON.parse(userStr);
      return user.name || user.email || 'Usuario';
    }
  } catch (err) {
    console.error('Error getting current user:', err);
  }
  return 'Usuario';
};

/**
 * Genera un ID único para la actividad
 */
const generateId = (): string => {
  return `activity_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Obtiene todas las actividades del localStorage
 */
export const getActivities = (): ActivityLog[] => {
  try {
    const stored = localStorage.getItem(ACTIVITY_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (err) {
    console.error('Error reading activities from localStorage:', err);
  }
  return [];
};

/**
 * Guarda actividades en localStorage
 */
const saveActivities = (activities: ActivityLog[]): void => {
  try {
    // Mantener solo las últimas MAX_ACTIVITIES
    const trimmed = activities.slice(0, MAX_ACTIVITIES);
    localStorage.setItem(ACTIVITY_STORAGE_KEY, JSON.stringify(trimmed));
  } catch (err) {
    console.error('Error saving activities to localStorage:', err);
  }
};

/**
 * Registra una nueva actividad
 */
export const logActivity = (
  module: ActivityModule,
  actionType: ActivityAction,
  entityName: string,
  entityId?: string | number,
  additionalMetadata?: Record<string, any>
): void => {
  try {
    const user = getCurrentUser();
    const actionText = (() => {
      switch (actionType) {
        case 'create':
          return module === 'Dashboards' ? 'Creó el dashboard' : 'Creó una fuente de datos';
        case 'update':
          return module === 'Dashboards' ? 'Actualizó el dashboard' : 'Actualizó una fuente de datos';
        case 'delete':
          return module === 'Dashboards' ? 'Eliminó el dashboard' : 'Eliminó una fuente de datos';
        default:
          return 'Realizó una acción';
      }
    })();

    const activity: ActivityLog = {
      id: generateId(),
      user,
      action: actionText,
      date: new Date().toISOString(),
      module,
      metadata: {
        name: entityName,
        type: actionType,
        entityId,
        ...additionalMetadata,
      },
    };

    const activities = getActivities();
    activities.unshift(activity); // Agregar al inicio
    saveActivities(activities);

    console.log('📝 Activity logged:', activity);
  } catch (err) {
    console.error('Error logging activity:', err);
  }
};

/**
 * Limpia todas las actividades (útil para testing o reset)
 */
export const clearActivities = (): void => {
  try {
    localStorage.removeItem(ACTIVITY_STORAGE_KEY);
  } catch (err) {
    console.error('Error clearing activities:', err);
  }
};

/**
 * Elimina actividades antiguas (más de X días)
 */
export const cleanOldActivities = (daysToKeep: number = 30): void => {
  try {
    const activities = getActivities();
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - daysToKeep);
    
    const filtered = activities.filter(activity => {
      const activityDate = new Date(activity.date);
      return activityDate >= cutoffDate;
    });
    
    saveActivities(filtered);
  } catch (err) {
    console.error('Error cleaning old activities:', err);
  }
};
